import { React, useState, useEffect, useContext, useRef } from "react";
import Button from "@mui/material/Button";
import { DataGrid } from '@mui/x-data-grid';
import GridNoRow from '../../../../common/GridNoRow';
import axios from "axios";
import Swal from "sweetalert2";
import Select from 'react-select';
import { AppContext } from '../../../../AppStore';
import { useNavigate, useParams } from "react-router-dom";
import TAB_StockInfoModal from "./TAB_StockInfoModal";

/**
 * 태블릿 재고 조회 화면 (범용, 조회 전용)
 *  - 기존 PC API 재사용: GET /api/p/StockInfo/List  (diff.v_item_stock_info)
 *  - 발주관리(TAB_Order) 와 동일한 슬림 헤더 필터 스타일
 *  - 행 클릭 → 그래프 모달(TAB_StockInfoModal): 일별 / 최근 / 월별 (< > 로 전환)
 *  - /t/stock_info/:item_id 로 진입해도 전체 목록 조회 + 해당 품목 모달 자동 오픈
 */
const TAB_StockInfo = () => {
    const navigate = useNavigate();
    const appStore = useContext(AppContext);
    const { item_id: paramItemID } = useParams();   // /t/stock_info/:item_id

    const [MenuID, setMenuID] = useState(0);
    const [MenuName, setMenuName] = useState('');

    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [typeOption, setTypeOption] = useState([]);
    const [divisionOption, setDivisionOption] = useState([]);
    const [keyword, setKeyword] = useState({ type: 0, division: 0, search: '' });

    // 그래프 모달
    const [modalOpen, setModalOpen] = useState(false);
    const [selectItem, setSelectItem] = useState({ id: '', name: '', spec: '' });
    const autoOpenRef = useRef(false);

    const availHeight = 'calc(100vh - 100px)';

    // 권한/메뉴
    useEffect(() => {
        const accessList = JSON.parse(localStorage.getItem("nav"));
        const currentPath = String(window.location.pathname).split("/").slice(0, 3).join("/");
        const isMatch = (url) => String(url) === currentPath || "/" + String(url) === currentPath;

        if (Array.isArray(accessList) && accessList.filter(item => isMatch(item.menu_url)).length > 0) {
            const matched = accessList.find(item => isMatch(item.menu_url));
            setMenuID(matched.menu_id);
            setMenuName(matched.menu_name);
        } else {
            Swal.fire({ title: '<p style="color:red">접근 권한 오류</p>', text: '화면에 접근 권한이 없습니다.', confirmButtonText: '확인', confirmButtonColor: '#7066e0', timer: 5000, timerProgressBar: true }).then(() => { navigate(-1); });
        }
    }, []);

    const handleSetting = () => {
        const numCell = (p) => (p.value == null || p.value === '') ? '0' : p.value;
        setColumns([
            { field: 'id', headerName: 'id', width: 0 },
            { field: 'item_type_name', headerName: '유형', width: 110, headerAlign: 'center', align: 'center' },
            { field: 'item_division_name', headerName: '분류', width: 120, headerAlign: 'center', align: 'center' },
            { field: 'item_name', headerName: '품목명', flex: 1, minWidth: 220, headerAlign: 'center', align: 'left' },
            { field: 'item_spec', headerName: '규격', flex: 1, minWidth: 200, headerAlign: 'center', align: 'left' },
            { field: 'dp_item_total_safe_stock_count', headerName: '안전재고', width: 140, headerAlign: 'center', align: 'right', renderCell: numCell },
            { field: 'dp_stock', headerName: '재고', width: 140, headerAlign: 'center', align: 'right', renderCell: numCell },
            { field: 'dp_use_stock', headerName: '가용재고', width: 150, headerAlign: 'center', align: 'right', renderCell: numCell },
        ]);
    };

    // 유형/분류 옵션
    const handleInitSelect = () => {
        axios.get('/api/Common/CodeDetailList', { withCredentials: true })
            .then((response) => {
                const type_array = [{ value: 0, label: '유형 전체' }];
                const division_array = [{ value: 0, label: '분류 전체' }];
                (response.data ?? []).forEach((d) => {
                    if (d.code_id === '0014') type_array.push({ value: d.code_detail_seq, label: d.code_detail_name });
                    else if (d.code_id === '0015') division_array.push({ value: d.code_detail_seq, label: d.code_detail_name });
                });
                setTypeOption(type_array);
                setDivisionOption(division_array);
            })
            .catch((err) => { appStore.capture ? appStore.capture() : console.log(err); });
    };

    // 재고 조회 (전체)
    const handleView = () => {
        axios.get('/api/p/StockInfo/List', {
            params: { type: keyword.type, division: keyword.division, search: keyword.search },
            withCredentials: true
        })
        .then((response) => {
            const array = (response.data ?? []).map((item) => ({
                id: item.item_id,
                item_type_name: item.item_type_name,
                item_division_name: item.item_division_name,
                item_name: item.item_name,
                item_spec: (appStore.ITEM_FULL_SPEC_MAKE
                    ? appStore.ITEM_FULL_SPEC_MAKE(item.item_spec, item.item_width, item.item_depth, item.item_height)
                    : (item.item_spec ?? '')),
                dp_item_total_safe_stock_count: item.dp_item_total_safe_stock_count,
                dp_stock: item.dp_stock,
                dp_use_stock: item.dp_use_stock,
            }));
            setRows(array);

            // ★ URL 파라미터(/t/stock_info/:item_id) 진입 시 전체 목록 유지 + 해당 품목 모달 자동 오픈
            if (paramItemID && !autoOpenRef.current) {
                const target = array.find((r) => String(r.id) === String(paramItemID));
                if (target) {
                    autoOpenRef.current = true;
                    openModal(target);
                }
            }
        })
        .catch((err) => { appStore.capture ? appStore.capture() : console.log(err); });
    };

    useEffect(() => {
        if (String(MenuID) !== String(0) && MenuID != null) {
            handleSetting();
            handleInitSelect();
            handleView();
        }
    }, [MenuID]);

    // 유형/분류 변경 시 자동 재조회
    useEffect(() => {
        if (String(MenuID) !== String(0) && MenuID != null) handleView();
    }, [keyword.type, keyword.division]);

    // 파라미터 바뀌면 자동 오픈 재허용
    useEffect(() => { autoOpenRef.current = false; }, [paramItemID]);

    const openModal = (row) => {
        setSelectItem({ id: row.id, name: row.item_name, spec: row.item_spec });
        setModalOpen(true);
    };

    const onSelectChange = (type, value) => setKeyword({ ...keyword, [type]: value });
    const onChange = (e) => setKeyword({ ...keyword, [e.target.name]: e.target.value });
    const onSubmitSearch = (e) => { if (e.key === 'Enter') handleView(); };
    const handleInit = () => setKeyword({ type: 0, division: 0, search: '' });

    return (
        <div style={{ display: String(MenuID) === String(0) ? 'none' : 'flex', flexDirection: 'column', width: '100%', height: availHeight, backgroundColor: '#fff', overflow: 'hidden' }}>

            {/* 상단 슬림형 검색바 (발주관리 동일 스타일) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid #ccc', backgroundColor: '#fff', minHeight: '60px', flexShrink: 0 }}>
                {/* 좌측 타이틀 */}
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333', display: 'flex', alignItems: 'center' }}>
                    <span style={{ width: '5px', height: '30px', backgroundColor: '#3D63AE', marginRight: '10px' }}></span>
                    {MenuName}
                </div>

                {/* 우측 검색조건 및 버튼 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '150px' }}>
                            <Select onChange={(e) => onSelectChange('type', e.value)} value={typeOption.filter(option => option.value === keyword.type)} options={typeOption} placeholder="유형" noOptionsMessage={() => "조회되는 항목 없음"} />
                        </div>
                        <div style={{ width: '150px' }}>
                            <Select onChange={(e) => onSelectChange('division', e.value)} value={divisionOption.filter(option => option.value === keyword.division)} options={divisionOption} placeholder="분류" noOptionsMessage={() => "조회되는 항목 없음"} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="text" className="btn-p" placeholder="검색" value={keyword.search} name="search" onChange={onChange} onKeyPress={onSubmitSearch} autoComplete="off" style={{ width: '200px', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 10px' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginLeft: '10px' }}>
                        <Button onClick={handleInit} className="btn-p" sx={{ fontSize: 14, minWidth: '70px', color: "white", backgroundColor: "black", borderColor: "black", ":hover": { backgroundColor: "black" } }} variant="outlined">초기화</Button>
                        <Button onClick={handleView} className="btn-p" sx={{ fontSize: 14, minWidth: '70px', color: "white", backgroundColor: "#3D63AE", borderColor: "#3D63AE", ":hover": { backgroundColor: "#3D63AE" } }} variant="outlined">조회</Button>
                    </div>
                </div>
            </div>

            {/* 재고 그리드 (행 클릭 → 그래프 모달) — 발주관리 동일 스타일 */}
            <div style={{ display: 'flex', flex: 1, width: '100%', overflow: 'hidden' }}>
                <div style={{ flex: 1, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        slots={{ noRowsOverlay: GridNoRow }}
                        columnVisibilityModel={{ id: false }}
                        AlternatingRowBackground="LavenderBlush"
                        rowsPerPageOptions={[20, 50, 100]}
                        onRowClick={(params) => openModal(params.row)}
                        disableRowSelectionOnClick
                        sx={{ height: '100%', border: 'none', '& .MuiDataGrid-cell': { cursor: 'pointer' } }}
                    />
                </div>
            </div>

            {modalOpen && (
                <TAB_StockInfoModal
                    onClose={() => setModalOpen(false)}
                    item_id={selectItem.id}
                    item_name={selectItem.name}
                    item_spec={selectItem.spec}
                />
            )}
        </div>
    );
};

export default TAB_StockInfo;
